/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { toolsetsAddExternalOAuthServer } from "../funcs/toolsetsAddExternalOAuthServer.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { Toolset } from "../models/components/toolset.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  AddExternalOAuthServerRequest,
  AddExternalOAuthServerSecurity,
} from "../models/operations/addexternaloauthserver.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type AddExternalOAuthServerMutationVariables = {
  request: AddExternalOAuthServerRequest;
  security?: AddExternalOAuthServerSecurity | undefined;
  options?: RequestOptions;
};

export type AddExternalOAuthServerMutationData = Toolset;

export type AddExternalOAuthServerMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * addExternalOAuthServer toolsets
 *
 * @remarks
 * Associate an external OAuth server with a toolset
 */
export function useAddExternalOAuthServerMutation(
  options?: MutationHookOptions<
    AddExternalOAuthServerMutationData,
    AddExternalOAuthServerMutationError,
    AddExternalOAuthServerMutationVariables
  >,
): UseMutationResult<
  AddExternalOAuthServerMutationData,
  AddExternalOAuthServerMutationError,
  AddExternalOAuthServerMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildAddExternalOAuthServerMutation(client, options),
    ...options,
  });
}

export function mutationKeyAddExternalOAuthServer(): MutationKey {
  return ["@gram/client", "toolsets", "addExternalOAuthServer"];
}

export function buildAddExternalOAuthServerMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: AddExternalOAuthServerMutationVariables,
  ) => Promise<AddExternalOAuthServerMutationData>;
} {
  return {
    mutationKey: mutationKeyAddExternalOAuthServer(),
    mutationFn: function addExternalOAuthServerMutationFn({
      request,
      security,
      options,
    }): Promise<AddExternalOAuthServerMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(toolsetsAddExternalOAuthServer(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
